/**
 * Hybrid Message component.
 *
 * Owns the compound-component wiring (MessageContext + sub-components + statics)
 * and routes each styled piece (root Container + Header/Body/Footer/Meta) between
 * its styled-components version (styles.ts) and its Tailwind version
 * (MessageTailwind.tsx), mirroring the Button hybrid pattern.
 *
 * Routing rule for each piece: use styled-components when the piece receives any
 * styled-system prop (hasStyledProps) OR a non-default `borderColor`. `borderColor`
 * is intentionally absent from STYLED_SYSTEM_PROPS, so it is guarded explicitly —
 * without the guard a custom border color would be dropped by the Tailwind path,
 * whose CSS hardcodes the base container colors. Everything else gets the lighter
 * Tailwind path.
 */

import * as React from "react";
import Avatar, { type TypeAvatarProps } from "@sproutsocial/seeds-react-avatar";
import Button, { type TypeButtonProps } from "@sproutsocial/seeds-react-button";
import Checkbox, {
  type TypeCheckboxProps,
} from "@sproutsocial/seeds-react-checkbox";
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import Container, {
  MessageBody as StyledMessageBody,
  MessageFooter as StyledMessageFooter,
  MessageHeader as StyledMessageHeader,
  MessageMeta as StyledMessageMeta,
} from "./styles";
import {
  MessageRootTailwind,
  MessageHeaderTailwind,
  MessageBodyTailwind,
  MessageFooterTailwind,
  MessageMetaTailwind,
} from "./MessageTailwind";
import { MESSAGE_DENSITIES } from "./constants";
import type { EnumDensities, TypeMessageProps } from "./MessageTypes";

const DEFAULT_BORDER_COLOR = "container.border.base";
const DEFAULT_BG = "container.background.base";

const avatarSizeMap = (density: EnumDensities) => {
  switch (density) {
    case MESSAGE_DENSITIES.LARGE:
      return "40px";

    case MESSAGE_DENSITIES.COMPACT:
      return "20px";

    case MESSAGE_DENSITIES.CONDENSED:
      return "24px";

    case MESSAGE_DENSITIES.SMALL:
    case MESSAGE_DENSITIES.MICRO:
    default:
      return "32px";
  }
};

const MessageContext = React.createContext<{
  density: EnumDensities;
  borderColor: TypeMessageProps["borderColor"];
  bg?: TypeMessageProps["bg"];
  indentContent?: boolean;
}>({
  density: MESSAGE_DENSITIES.SMALL,
  borderColor: "container.border.base",
  bg: "container.background.base",
  indentContent: true,
});

const Message = ({
  children,
  density = MESSAGE_DENSITIES.SMALL,
  borderColor = "container.border.base",
  bg = "container.background.base",
  indentContent = true,
  innerRef,
  qa,
  viewing,
  unread,
  indicator,
  ...rest
}: TypeMessageProps) => {
  // Route to styled-components when any styled-system prop is present, or when a
  // non-default bg / borderColor is requested (the Tailwind path only renders the
  // base container colors). `bg` already trips hasStyledProps, but is kept here
  // for clarity alongside the borderColor guard.
  const useStyledComponents =
    hasStyledProps(rest) ||
    bg !== DEFAULT_BG ||
    borderColor !== DEFAULT_BORDER_COLOR;

  const isMicro = density === MESSAGE_DENSITIES.MICRO;
  const { onClick, onKeyDown } = rest;

  const microProps = isMicro
    ? {
        "aria-current": viewing || undefined,
        "data-viewing": viewing || undefined,
        ...(onClick && {
          role: "button",
          tabIndex: 0,
          onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
            onKeyDown?.(e);
            if (
              e.target === e.currentTarget &&
              (e.key === "Enter" || e.key === " ")
            ) {
              e.preventDefault();
              e.currentTarget.click();
            }
          },
        }),
      }
    : {};

  const content = (
    <>
      {children}
      {isMicro && unread && <span className="seeds-message-unread" />}
      {isMicro && indicator && (
        <span className="seeds-message-indicator">{indicator}</span>
      )}
    </>
  );

  return (
    <MessageContext.Provider
      value={{
        density,
        borderColor,
        indentContent,
      }}
    >
      {useStyledComponents ? (
        <Container
          density={density}
          borderColor={borderColor}
          bg={bg}
          ref={innerRef}
          {...qa}
          {...rest}
          {...microProps}
        >
          {content}
        </Container>
      ) : (
        <MessageRootTailwind
          density={density}
          ref={innerRef}
          {...qa}
          {...rest}
          {...microProps}
        >
          {content}
        </MessageRootTailwind>
      )}
    </MessageContext.Provider>
  );
};

const MessageBody = (props: TypeMessageProps) => (
  <MessageContext.Consumer>
    {({ density, borderColor, indentContent }) =>
      density === MESSAGE_DENSITIES.MICRO ? null : hasStyledProps(props) ||
        borderColor !== DEFAULT_BORDER_COLOR ? (
        <StyledMessageBody
          data-qa-message-body
          density={density}
          borderColor={borderColor}
          indentContent={indentContent}
          {...props}
        />
      ) : (
        <MessageBodyTailwind
          density={density}
          indentContent={indentContent}
          {...props}
        />
      )
    }
  </MessageContext.Consumer>
);

MessageBody.displayName = "Message.Body";
Message.Body = MessageBody;

const MessageHeader = (props: TypeMessageProps) => (
  <MessageContext.Consumer>
    {({ density, borderColor }) =>
      hasStyledProps(props) || borderColor !== DEFAULT_BORDER_COLOR ? (
        <StyledMessageHeader
          density={density}
          borderColor={borderColor}
          {...props}
        />
      ) : (
        <MessageHeaderTailwind density={density} {...props} />
      )
    }
  </MessageContext.Consumer>
);

MessageHeader.displayName = "Message.Header";
Message.Header = MessageHeader;

const MessageFooter = (props: TypeMessageProps) => (
  <MessageContext.Consumer>
    {({ density, borderColor, indentContent }) =>
      density === MESSAGE_DENSITIES.MICRO ? null : hasStyledProps(props) ||
        borderColor !== DEFAULT_BORDER_COLOR ? (
        <StyledMessageFooter
          density={density}
          borderColor={borderColor}
          indentContent={indentContent}
          {...props}
        />
      ) : (
        <MessageFooterTailwind
          density={density}
          indentContent={indentContent}
          {...props}
        />
      )
    }
  </MessageContext.Consumer>
);

MessageFooter.displayName = "Message.Footer";
Message.Footer = MessageFooter;

const MessageMeta = (props: TypeMessageProps) => (
  <MessageContext.Consumer>
    {({ density, borderColor, indentContent }) =>
      density === MESSAGE_DENSITIES.MICRO ? null : hasStyledProps(props) ||
        borderColor !== DEFAULT_BORDER_COLOR ? (
        <StyledMessageMeta
          density={density}
          borderColor={borderColor}
          indentContent={indentContent}
          {...props}
        />
      ) : (
        <MessageMetaTailwind
          density={density}
          indentContent={indentContent}
          {...props}
        />
      )
    }
  </MessageContext.Consumer>
);

MessageMeta.displayName = "Message.Meta";
Message.Meta = MessageMeta;

const MessageButton = (props: TypeButtonProps) => (
  <Button {...props} appearance={props.appearance || "pill"} />
);

MessageButton.displayName = "Message.Button";
Message.Button = MessageButton;

const MessageAvatar = (props: TypeAvatarProps) => (
  <MessageContext.Consumer>
    {({ density }) => <Avatar size={avatarSizeMap(density)} {...props} />}
  </MessageContext.Consumer>
);

MessageAvatar.displayName = "Message.Avatar";
Message.Avatar = MessageAvatar;

const MessageCheckbox = (props: TypeCheckboxProps) => (
  <MessageContext.Consumer>
    {({ density }) => (
      <Checkbox
        appearance="pill"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - `density` is not a valid prop for `Checkbox`
        density={density} // @ts-notes - not sure what's intended here so address during refactor
        {...props}
      />
    )}
  </MessageContext.Consumer>
);

MessageCheckbox.displayName = "Message.Checkbox";
Message.Checkbox = MessageCheckbox;

Message.Context = MessageContext;
Message.DENSITIES = MESSAGE_DENSITIES;

export default Message;
